import { last } from 'lodash';

import { i18n } from '@apps/main/modules/localization/localizationSetup';
import { getImageUrl } from '@apps/main/modules/api/hotels';
import { isAvailableFlightTransportIngredientResponseWithStaticInfo } from '@apps/main/modules/recipe/ingredients';

import { getShortDatePeriod, isDefined } from '@libs/utils';
import { DateFormat, SemboDate } from '@libs/sembo-dates';
import { IngredientType } from '@libs/trip-planner-interfaces';
import type {
  Money,
  ChronologicalItinerary,
  FerryIngredientResponse,
  FlightIngredientResponse,
  ItineraryWithPrice,
  FerryTicketCategory,
  ItineraryMainIngredientResponseWithStaticInfo,
  ItineraryWithStaticInfo,
  Coordinate,
} from '@libs/trip-planner-interfaces';
import {
  isAvailableHotelIngredientResponseWithStaticInfo,
  isAvailableFerryIngredientResponse,
  isAvailableFlightIngredientResponse,
  isOwnStayIngredientResponseWithStaticInfo,
  isFlightIngredientResponseWithStaticInfo,
  isHotelIngredientResponseWithStaticInfo,
} from '@libs/search-service-smart-search';
import type { AppButtonState } from '@libs/ui';
import { getDistanceBetweenCoordinates } from '@libs/maps';

import type {
  TripSummarySegmentPrice,
  TripSummarySegment,
  TripSummaryPrice,
  TripSummaryActivitySegment,
  TripSummaryFerrySegment,
  TripSummaryFlightSegment,
  TripSummaryHotelSegment,
} from '../../models';
import { ItinerarySegmentType, TripSummaryPriceType } from '../../models';
import {
  getFlightDescriptionFromNumberOfStops,
  getMainCarrierStaticInfo,
  getFerryCarrierLogo,
} from '../tripOverviewSegments/tripOverviewBookableTransportSegmentLogic';
import { REQUSTED_IMAGE_SIZE } from '../tripOverviewSegments/tripOverviewHotelRelatedSegmentsLogic';

const { t } = i18n.global;

const ingredientTypeTranslation: Partial<Record<IngredientType, string>> = {
  [IngredientType.Flight]: t('planner.transport.flight'),
  [IngredientType.Ferry]: t('planner.transport.ferry'),
  [IngredientType.Activity]: t('planner.activity'),
  [IngredientType.Hotel]: t('planner.hotel'),
};

function getIngredientTypeTranslation(ingredientType: IngredientType): string {
  const translation = ingredientTypeTranslation[ingredientType];

  if (!translation) {
    throw new Error(
      `Could not find a translation for ${ingredientType} ingredient type!`
    );
  }

  return translation || ingredientType;
}

function getTransportDirection(
  isSingleDestinationTrip: boolean,
  chronologicalItinerary: ChronologicalItinerary[],
  currentItineraryIndex: number
): string | undefined {
  if (!isSingleDestinationTrip) {
    return undefined;
  }

  const accommodationDayIndex = chronologicalItinerary.findIndex((it) =>
    it.ingredients.some(
      (ing) =>
        isAvailableHotelIngredientResponseWithStaticInfo(ing) ||
        isOwnStayIngredientResponseWithStaticInfo(ing)
    )
  );

  if (accommodationDayIndex === -1) {
    return undefined;
  }

  return currentItineraryIndex <= accommodationDayIndex
    ? t('filter.flightFilters.departure')
    : t('filter.flightFilters.arrival');
}

function createAccommodationSegment(
  ingredient: ItineraryMainIngredientResponseWithStaticInfo,
  moneyFormatterFn: (amount: number, currency: string) => string
): TripSummaryHotelSegment | null {
  if (!isAvailableHotelIngredientResponseWithStaticInfo(ingredient)) {
    return null;
  }

  return {
    summaryType: ItinerarySegmentType.Accommodation,
    title: getIngredientTypeTranslation(ingredient.ingredientType),
    price: [
      {
        description: ingredient.staticInfo.name,
        value: moneyFormatterFn(
          ingredient.suggested.totalPrice.amount,
          ingredient.suggested.totalPrice.currency
        ),
        amount: ingredient.suggested.totalPrice.amount,
        type: 'ingredient',
      },
    ],
    images: ingredient.staticInfo.images.map((image) =>
      getImageUrl(image, undefined, REQUSTED_IMAGE_SIZE, REQUSTED_IMAGE_SIZE)
    ),
    nights: ingredient.numberOfNights,
    date: getShortDatePeriod(
      ingredient.date,
      new SemboDate(ingredient.date)
        .add({ days: ingredient.numberOfNights })
        .get(),
      false,
      true
    ),
    location: ingredient.staticInfo.mainPolygon?.fullName,
  };
}

function createActivitySegments(
  ingredient: ItineraryMainIngredientResponseWithStaticInfo,
  moneyFormatterFn: (amount: number, currency: string) => string
): TripSummaryActivitySegment[] {
  if (
    !isAvailableHotelIngredientResponseWithStaticInfo(ingredient) &&
    !isOwnStayIngredientResponseWithStaticInfo(ingredient)
  ) {
    return [];
  }

  return ingredient.subIngredientResponseWithStaticInfo.flatMap(
    (subingredient) => {
      if (
        !subingredient.suggested?.totalPrice ||
        !subingredient.staticInfo ||
        !subingredient.suggested?.tickets
      ) {
        return [];
      }

      return [
        {
          summaryType: ItinerarySegmentType.Activity,
          title: getIngredientTypeTranslation(subingredient.ingredientType),
          price: [
            {
              description: subingredient.staticInfo.name,
              value: moneyFormatterFn(
                subingredient.suggested.totalPrice.amount,
                subingredient.suggested.totalPrice.currency
              ),
              amount: subingredient.suggested.totalPrice.amount,
              type: 'ingredient',
            },
          ],
          images: subingredient.staticInfo.images.map(
            (image) => image.imageName
          ),
          numberOfTickets: subingredient.suggested.tickets.reduce(
            (sum, ticket) => sum + ticket.quantity,
            0
          ),
          date: new SemboDate(subingredient.date).format(
            DateFormat.DateWithShortMonth
          ),
          nights: undefined,
          location: subingredient.staticInfo.mainPolygon?.fullName,
        },
      ];
    }
  );
}

function createFlightSegment(
  ingredient: ItineraryMainIngredientResponseWithStaticInfo,
  isSingleDestinationTrip: boolean,
  chronologicalItinerary: ChronologicalItinerary[],
  itineraryIndex: number,
  moneyFormatterFn: (amount: number, currency: string) => string,
  isMobile: boolean
): TripSummaryFlightSegment | null {
  if (!isAvailableFlightIngredientResponse(ingredient)) {
    return null;
  }

  const priceValue =
    ingredient.legIndex === 0
      ? moneyFormatterFn(
          ingredient.totalPrice.amount,
          ingredient.totalPrice.currency
        )
      : t('planner.included').toLowerCase();

  const priceAmount =
    ingredient.legIndex === 0 ? ingredient.totalPrice.amount : 0;
  const staticCarriers = getMainCarrierStaticInfo(ingredient);

  const transportDirection = getTransportDirection(
    isSingleDestinationTrip,
    chronologicalItinerary,
    itineraryIndex
  );

  const departureIata =
    ingredient.staticInfo.ports[ingredient.departureIata]?.position.name;
  const arrivalIata =
    ingredient.staticInfo.ports[ingredient.arrivalIata]?.position.name;
  const departureIataText = departureIata
    ? `${departureIata} ${isMobile ? '' : `(${ingredient.departureIata})`}`
    : '';
  const arrivalIataText = arrivalIata
    ? `${arrivalIata} ${isMobile ? '' : `(${ingredient.arrivalIata})`}`
    : '';
  return {
    summaryType: ItinerarySegmentType.Flight,
    title: getIngredientTypeTranslation(ingredient.ingredientType),
    transportDirection,
    price: [
      {
        description: `${departureIataText} - ${arrivalIataText}`,
        value: priceValue,
        amount: priceAmount,
        type: 'ingredient',
      },
    ],
    carrier: staticCarriers?.name,
    images: [staticCarriers?.logo ?? ''],
    date: new SemboDate(ingredient.date).format(DateFormat.DateWithShortMonth),
    stops: getFlightDescriptionFromNumberOfStops(ingredient.numberOfStops),
    time: `${new SemboDate(ingredient.departureDate).format(
      DateFormat.Time
    )} - ${new SemboDate(ingredient.arrivalDate).format(DateFormat.Time)}`,
  };
}

interface CreateFerrySegmentParams {
  ingredient: ItineraryMainIngredientResponseWithStaticInfo;
  isSingleDestinationTrip: boolean;
  chronologicalItinerary: ChronologicalItinerary[];
  itineraryIndex: number;
  numberOfTravelers: number;
  addons: Record<string, FerryTicketCategory>;
  moneyFormatterFn: (amount: number, currency: string) => string;
}

function createFerrySegment(
  config: CreateFerrySegmentParams
): TripSummaryFerrySegment | null {
  const {
    ingredient,
    isSingleDestinationTrip,
    chronologicalItinerary,
    itineraryIndex,
    numberOfTravelers,
    addons,
    moneyFormatterFn,
  } = config;
  if (!isAvailableFerryIngredientResponse(ingredient)) {
    return null;
  }

  const transportDirection = getTransportDirection(
    isSingleDestinationTrip,
    chronologicalItinerary,
    itineraryIndex
  );

  const price: TripSummarySegmentPrice[] = [
    {
      description: `${
        ingredient.staticInfo.ports[ingredient.departureIata]?.position.name
      } - ${
        ingredient.staticInfo.ports[ingredient.arrivalIata]?.position.name
      }`,
      value: moneyFormatterFn(
        ingredient.totalPrice.amount,
        ingredient.totalPrice.currency
      ),
      amount: ingredient.totalPrice.amount,
      type: 'ingredient',
    },
  ];

  const addon = addons[ingredient.addToBasketToken];
  if (addon) {
    const addonPrice: Money = {
      ...addon.price,
      amount: addon.price.amount - ingredient.totalPrice.amount,
    };
    if (addonPrice.amount > 0) {
      price.push({
        description: `${numberOfTravelers} ${addon.type} ${t(
          'planner.tickets',
          numberOfTravelers
        )}`.toLowerCase(),
        value: moneyFormatterFn(addonPrice.amount, addonPrice.currency),
        amount: addonPrice.amount,
        type: 'addon',
      });
    }
  }

  return {
    summaryType: ItinerarySegmentType.Ferry,
    title: getIngredientTypeTranslation(ingredient.ingredientType),
    transportDirection,
    price,
    date: new SemboDate(ingredient.date).format(DateFormat.DateWithShortMonth),
    time: `${new SemboDate(
      ingredient.legs[0]?.segments[0]?.departureDateTime
    ).format(DateFormat.Time)} - ${new SemboDate(
      ingredient.legs[0]?.segments[0]?.arrivalDateTime
    ).format(DateFormat.Time)}`,
    images: getFerryCarrierLogo(ingredient).urls,
    carrier: ingredient.transportationName,
  };
}

interface GetTripSummaryPriceSegmentsParams {
  chronologicalItinerary: ChronologicalItinerary[];
  numberOfTravelers: number;
  addons: Record<string, FerryTicketCategory>;
  moneyFormatterFn: (amount: number, currency: string) => string;
  numberOfDestinations: number;
  isMobile: boolean;
}

export function getTripSummaryPriceSegments(
  params: GetTripSummaryPriceSegmentsParams
): TripSummarySegment[] {
  if (params.chronologicalItinerary.length === 0) {
    return [];
  }

  const isSingleDestinationTrip = params.numberOfDestinations === 1;

  return params.chronologicalItinerary.reduce(
    (
      acc: TripSummarySegment[],
      itinerary: ChronologicalItinerary,
      itineraryIndex: number
    ) => {
      for (const ingredient of itinerary.ingredients) {
        const accommodationSegment = createAccommodationSegment(
          ingredient,
          params.moneyFormatterFn
        );
        if (accommodationSegment) {
          acc.push(accommodationSegment);
        }

        const activitySegments = createActivitySegments(
          ingredient,
          params.moneyFormatterFn
        );
        acc.push(...activitySegments);

        const flightSegment = createFlightSegment(
          ingredient,
          isSingleDestinationTrip,
          params.chronologicalItinerary,
          itineraryIndex,
          params.moneyFormatterFn,
          params.isMobile
        );
        if (flightSegment) {
          acc.push(flightSegment);
        }

        const ferrySegment = createFerrySegment({
          ingredient,
          isSingleDestinationTrip,
          chronologicalItinerary: params.chronologicalItinerary,
          itineraryIndex,
          numberOfTravelers: params.numberOfTravelers,
          addons: params.addons,
          moneyFormatterFn: params.moneyFormatterFn,
        });
        if (ferrySegment) {
          acc.push(ferrySegment);
        }
      }

      return acc;
    },
    []
  );
}

export function isEveryIngredientAvailableFn(
  itinerary: ItineraryWithPrice | undefined
): boolean {
  if (!itinerary) {
    return false;
  }

  const isEveryFerryTransportAvailable = itinerary.ferryOnlyItinerary
    .map((ferry: FerryIngredientResponse) => ferry.isAvailable)
    .every((isAvailable: boolean) => isAvailable);

  if (!isEveryFerryTransportAvailable) {
    return false;
  }

  const isEveryFlightTransportAvailable = itinerary.flightOnlyItinerary
    .map((flight: FlightIngredientResponse) => flight.isAvailable)
    .every((isAvailable: boolean) => isAvailable);

  if (!isEveryFlightTransportAvailable) {
    return false;
  }

  const isEveryHotelAvailable = itinerary.hotelOnlyItinerary.every(
    (hotel) => hotel.isAvailable
  );

  if (!isEveryHotelAvailable) {
    return false;
  }

  const isEveryActivityAvailable = [
    ...itinerary.hotelOnlyItinerary,
    ...itinerary.ownStayOnlyItinerary,
  ]
    .flatMap((stay) => stay.subIngredientResponse)
    .every((activity) => activity.isAvailable);

  return isEveryActivityAvailable;
}

interface GetTripSummaryPriceParams {
  totalPrice: Money | null | undefined;
  totalPriceWithAlternative: Money | null | undefined;
  grossPrice: Money | null | undefined;
  grossPriceWithAlternative: Money | null | undefined;
}

export function getTripSummaryPrice(
  params: GetTripSummaryPriceParams
): TripSummaryPrice | undefined {
  if (params.totalPrice && !params.grossPrice) {
    return {
      totalPrice: params.totalPrice,
      grossPrice: undefined,
      priceType: TripSummaryPriceType.Total,
    };
  }

  if (params.totalPrice && params.grossPrice) {
    return {
      totalPrice: params.totalPrice,
      grossPrice: params.grossPrice,
      priceType: TripSummaryPriceType.TotalGross,
    };
  }

  if (params.totalPriceWithAlternative && !params.grossPriceWithAlternative) {
    return {
      totalPrice: params.totalPriceWithAlternative,
      grossPrice: undefined,
      priceType: TripSummaryPriceType.TotalAlternative,
    };
  }

  if (params.totalPriceWithAlternative && params.grossPriceWithAlternative) {
    return {
      totalPrice: params.totalPriceWithAlternative,
      grossPrice: params.grossPriceWithAlternative,
      priceType: TripSummaryPriceType.TotalGrossAlternative,
    };
  }

  return undefined;
}

export function getBookingButtonState(
  bookingInProgress: boolean,
  isEveryIngredientAvailable: boolean
): AppButtonState {
  if (bookingInProgress) {
    return 'loadingDots';
  }

  if (isEveryIngredientAvailable) {
    return 'enabled';
  }

  return 'disabled';
}

export function getTripTotalPrice(
  tripSummaryPrice: TripSummaryPrice | undefined
): number {
  return tripSummaryPrice?.totalPrice?.amount ?? 0;
}

export function getTripGrossPrice(
  tripSummaryPrice: TripSummaryPrice | undefined
): number {
  return tripSummaryPrice?.grossPrice?.amount ?? 0;
}

export function checkAirportDistance(
  itineraryWithStaticInfo?: ItineraryWithStaticInfo,
  distanceThresholdKm = 60
):
  | {
      isBeyondThreshold: false;
    }
  | {
      isBeyondThreshold: true;
      airportIata: string;
      airportName?: string;
      distanceKm: number;
      hotelPolygonId?: number;
      hotelName?: string;
      isArrival: boolean;
    } {
  if (!itineraryWithStaticInfo) {
    return { isBeyondThreshold: false };
  }

  for (const [
    index,
    ingredient,
  ] of itineraryWithStaticInfo.itinerary.entries()) {
    if (
      !isHotelIngredientResponseWithStaticInfo(ingredient) ||
      !isDefined(ingredient.staticInfo)
    ) {
      continue;
    }

    const hotelCoordinates: Coordinate = {
      latitude: ingredient.staticInfo.position.latitude.decimalDegrees,
      longitude: ingredient.staticInfo.position.longitude.decimalDegrees,
    };

    const previousIngredient:
      | ItineraryMainIngredientResponseWithStaticInfo
      | undefined = itineraryWithStaticInfo.itinerary[index - 1];
    if (
      isDefined(previousIngredient) &&
      isDefined(previousIngredient.staticInfo) &&
      isFlightIngredientResponseWithStaticInfo(previousIngredient)
    ) {
      const legIndex: number | undefined =
        isAvailableFlightTransportIngredientResponseWithStaticInfo(
          previousIngredient
        )
          ? previousIngredient.legIndex
          : previousIngredient.alternativeSuggestion?.legIndex;

      const arrivalPort = isDefined(legIndex)
        ? last(previousIngredient.legs?.[legIndex]?.segments)?.arrivalIata
        : undefined;

      const arrivalPortCoordinates = isDefined(arrivalPort)
        ? previousIngredient.staticInfo.ports[arrivalPort]?.position
        : undefined;

      if (isDefined(arrivalPort) && isDefined(arrivalPortCoordinates)) {
        const distanceToPreviousAirport = getDistanceBetweenCoordinates(
          hotelCoordinates,
          arrivalPortCoordinates
        );

        if (distanceToPreviousAirport > distanceThresholdKm) {
          return {
            isBeyondThreshold: true,
            distanceKm: distanceToPreviousAirport,
            airportIata: arrivalPort,
            airportName: arrivalPortCoordinates.name,
            hotelPolygonId: ingredient.staticInfo.mainPolygon?.id,
            hotelName: ingredient.staticInfo.name,
            isArrival: true,
          };
        }
      }
    }

    const nextIngredient:
      | ItineraryMainIngredientResponseWithStaticInfo
      | undefined = itineraryWithStaticInfo.itinerary[index + 1];
    if (
      isDefined(nextIngredient) &&
      isDefined(nextIngredient.staticInfo) &&
      isFlightIngredientResponseWithStaticInfo(nextIngredient)
    ) {
      const legIndex: number | undefined =
        isAvailableFlightTransportIngredientResponseWithStaticInfo(
          nextIngredient
        )
          ? nextIngredient.legIndex
          : nextIngredient.alternativeSuggestion?.legIndex;

      const departurePort = isDefined(legIndex)
        ? nextIngredient.legs?.[legIndex]?.segments[0]?.departureIata
        : undefined;

      const departurePortCoordinates = isDefined(departurePort)
        ? nextIngredient.staticInfo.ports[departurePort]?.position
        : undefined;

      if (isDefined(departurePort) && isDefined(departurePortCoordinates)) {
        const distanceToNextAirport = getDistanceBetweenCoordinates(
          hotelCoordinates,
          departurePortCoordinates
        );

        if (distanceToNextAirport > distanceThresholdKm) {
          return {
            isBeyondThreshold: true,
            distanceKm: distanceToNextAirport,
            airportIata: departurePort,
            airportName: departurePortCoordinates.name,
            hotelPolygonId: ingredient.staticInfo.mainPolygon?.id,
            hotelName: ingredient.staticInfo.name,
            isArrival: false,
          };
        }
      }
    }
  }

  return { isBeyondThreshold: false };
}
